import { LocalDate, nativeJs } from '@js-joda/core';

import { AvailabilityCreateCommand, AvailabilityOverlappingFault } from '@dreryk/egabinet-appointment-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { createSingleInterpolatedTranslationKey } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { getRecurrencePeriodInWeeks } from '../../common/common.utils';
import { personnelTranslationKeys } from '../../translations';
import {
   TIME_SLOT_NONE,
   TIME_SLOT_OTHER,
} from '../common/fields/AvailabilityTimeSlot/AvailabilityTimeSlotFixed/AvailabilityTimeSlotFixed.types';
import { getBookingChannels } from '../common/common.utils';

import { AvailabilityCreationFormBean } from './availabilityCreation.types';

export const useCreateAvailabilityCommand = (
   practitionerId: PractitionerId,
   onSuccess: VoidFunction,
   onClose: () => void
): UseCqrsCommandResult<void, AvailabilityCreationFormBean> => {
   return useCqrsCommand<void, AvailabilityCreationFormBean, AvailabilityOverlappingFault>(
      (parameters: AvailabilityCreationFormBean) => {
         const command = new AvailabilityCreateCommand();
         const slotDuration =
            parameters.slotDurationFixed === TIME_SLOT_OTHER
               ? parameters.slotDurationManual
               : parameters.slotDurationFixed;
         command.availabilityDate = parameters.availabilityDate;
         command.availabilityTypeId = Number(parameters.availabilityTypeId!.id);
         command.startTime = parameters.startTime;
         command.endTime = parameters.endTime;
         command.recurrencePeriodInWeeks =
            parameters.recurrencePeriod === 'NONE'
               ? undefined
               : Choice.of(getRecurrencePeriodInWeeks(parameters.recurrencePeriod));
         command.slotDurationInMinutes = Choice.withUseDefaultIfNull(
            slotDuration === TIME_SLOT_NONE ? null : slotDuration
         );
         command.practitionerId = practitionerId;
         command.bookingChannels = getBookingChannels(parameters);
         command.location = Choice.withUseDefaultIfNull(
            parameters.healthcareProviderLocation!.locationIdentifier ?? null
         );
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(personnelTranslationKeys.successfullyCreatedAvailability);
            onClose();
            onSuccess();
         },
         onFault: {
            'egabinet-appointment-frontend-api:AvailabilityOverlappingFault': (fault) =>
               dispatchSnackbarNegativeEvent(
                  createSingleInterpolatedTranslationKey(
                     personnelTranslationKeys.overlappingAvailabilityError,
                     'conflictDate',
                     LocalDate.from(nativeJs(new Date(`${fault.overlappingAvailabilityDates[0]}`))).toString()
                  )
               ),
         },
      }
   );
};
